import React, { useEffect, useMemo, useRef, useState } from 'react'
import phoneIcon from '../../img/phoneIcon.svg'
import messageIcon from '../../img/messageIcon.svg'
import CreateTicketPage from './CreateTicketPage'
import RemoteSessionTab from '../remoteUnlock/RemoteSessionTab'
import { fetchTicketAnswers, REMOTE_SESSION_TAB, TD_TAB_HANDOFF_KEY } from '../remoteUnlock/remoteUnlockApi'
import useRemoteSessionLevels from '../remoteUnlock/useRemoteSessionLevels'
import {
  ERT_SOC_OPTIONS, ERT_BP_CHECKED_AT_OPTIONS, ERT_TOOL_USED_OPTIONS,
  ERT_PHYSICAL_FAULT_OPTIONS, ERT_REMOTE_FAULT_OPTIONS, ERT_BD_ID_REQUIRED_TOOLS,
} from '../../constants/ticketFormOptions'

const TABS = [
  { id: 'overview',        label: 'Overview' },
  { id: 'timeline',        label: 'Timeline' },
  { id: 'communications',  label: 'Communications' },
  { id: 'followups',       label: 'Follow-ups' },
  { id: 'evidence',        label: 'Evidence' },
  { id: 'location',        label: 'Location' },
  { id: 'swaps',           label: 'Swap Details' },
  { id: REMOTE_SESSION_TAB, label: 'Remote Session' },   // shown only for checklist tickets
]

const TEAM_LABELS = {
  ert:              'ERT',
  customer_support: 'Customer Support',
  station:          'Station',
  dco:              'DCO',
  aar:              'AAR',
  after_sales:      'After Sales',
}

const formatFieldLabel = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

const STATUS_OPTIONS = [
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold',     label: 'On Hold' },
  { value: 'closed',      label: 'Closed' },
  { value: 're_opened',   label: 'Re-Opened' },
]

const displayStatus = (s) => {
  if (!s) return '—'
  const map = { in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed', on_hold: 'On Hold', re_opened: 'Re-Opened', resolved_verification_pending: 'Resolved - Verification Pending' }
  return map[s] || s
}
const displayPriority = (p) => p ? p.charAt(0).toUpperCase() + p.slice(1) : null
const displayChannel = (c) => {
  if (!c) return '—'
  const map = { call: 'Phone Call (IVR)', whatsapp: 'WhatsApp', rider_app: 'Rider App', email: 'Email', chat: 'Chat' }
  return map[c] || c.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())
}
const LOCATION_TYPE_LABELS = { created: 'Ticket Created Location', creation: 'Ticket Created Location', closure: 'Ticket Closure Location', closed: 'Ticket Closure Location' }
const locationTypeLabel = (t) => {
  const key = String(t || '').toLowerCase()
  if (LOCATION_TYPE_LABELS[key]) return LOCATION_TYPE_LABELS[key]
  return t ? `${String(t).replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())} Location` : 'Location'
}
const statusClass = (s) => {
  if (s === 'in_progress') return 'in-progress'
  if (s === 'resolved')    return 'resolved'
  if (s === 'resolved_verification_pending') return 'verification-pending'
  if (s === 'on_hold')     return 'on-hold'
  if (s === 'closed')      return 'closed'
  if (s === 're_opened')   return 're-opened'
  return 'in-progress'
}
const formatExperience = (v) => {
  if (!v) return '—'
  const map = { '0_15_days': '<15 days', less_than_5_years: '<5yrs', '5_plus_years': '5+ yrs' }
  return map[v] || v.replace(/_/g, ' ')
}
const formatConfidence = (v) => {
  if (!v) return null
  const n = String(v).toLowerCase()
  if (n.includes('high'))   return { label: 'High',   className: 'high' }
  if (n.includes('medium')) return { label: 'Medium', className: 'medium' }
  if (n.includes('low'))    return { label: 'Low',    className: 'low' }
  return { label: v, className: 'medium' }
}
const formatRisk = (v) => {
  if (!v) return null
  const n = String(v).toLowerCase()
  if (n.includes('low'))  return { label: 'Low',  className: 'low-risk' }
  if (n.includes('high')) return { label: 'High', className: 'high-risk' }
  return { label: v, className: 'medium' }
}
const dash = (v) => (v === null || v === undefined || v === '') ? '—' : v

const TICKET_ID_PARAM = 'ticketId'
function getTicketIdFromUrl() {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get(TICKET_ID_PARAM) || null
}
function getInitialTabFromUrl() {
  if (typeof window === 'undefined') return 'overview'
  // one-shot hand-off from CreateTicketPage (Remote Battery Unlock → Step 2)
  try {
    const handoff = sessionStorage.getItem(TD_TAB_HANDOFF_KEY)
    if (handoff) {
      sessionStorage.removeItem(TD_TAB_HANDOFF_KEY)
      if (TABS.some(t => t.id === handoff)) return handoff
    }
  } catch { /* storage unavailable */ }
  const tab = new URLSearchParams(window.location.search).get('tab')
  return TABS.some(t => t.id === tab) ? tab : 'overview'
}

/* ── format minutes → "Xh Ym" or "Zm" ── */
function formatMins(mins) {
  if (mins === null || mins === undefined) return '—'
  const m = Math.round(Math.abs(mins))
  const h = Math.floor(m / 60)
  const rem = m % 60
  return h > 0 ? `${h}h ${rem}m` : `${m}m`
}

/* ── time between two timeline events ── */
function timeDiff(from, to) {
  const d1 = new Date(from), d2 = new Date(to)
  if (isNaN(d1) || isNaN(d2) || d2 < d1) return null
  const mins = Math.round((d2 - d1) / 60000)
  if (mins < 1)  return '< 1 min'
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60), m = mins % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

/* ── timeline dot colour ── */
function timelineColor(title) {
  const t = (title || '').toLowerCase()
  if (t.includes('creat'))                       return '#3B82F6'
  if (t.includes('assign'))                      return '#10B981'
  if (t.includes('progress'))                    return '#F59E0B'
  if (t.includes('hold'))                        return '#6B7280'
  if (t.includes('resolv'))                      return '#22C55E'
  if (t.includes('clos'))                        return '#94A3B8'
  if (t.includes('re-open') || t.includes('reopen')) return '#EF4444'
  return '#9CA3AF'
}

function InfoRow({ label, value, children }) {
  return (
    <div className="td-row">
      <span className="td-row-label">{label}</span>
      <span className="td-row-value">{children || dash(value)}</span>
    </div>
  )
}

function SectionCard({ title, children }) {
  return (
    <div className="td-card">
      <div className="td-card-title">{title}</div>
      {children}
    </div>
  )
}

/* ── TAT Details (replaces SLA Tracker) ── */
function TatDetails({ tat, stage }) {
  const remaining = tat?.remaining_minutes ?? null
  const total     = tat?.tat_minutes       ?? null
  const elapsed   = tat?.elapsed_minutes   ?? null
  const level     = (tat?.level || stage || 'l1').toUpperCase()
  const progress  = (total > 0 && elapsed !== null)
    ? Math.min(100, Math.max(0, (elapsed / total) * 100))
    : 0
  const isOverdue = remaining !== null && remaining <= 0

  return (
    <div className="td-card td-card-sidebar td-sla-card">
      <div className="td-card-title">SLA Details</div>
      <div className="td-sla-timer" style={{ color: isOverdue ? '#DC2626' : '#D97706' }}>
        {remaining !== null ? formatMins(remaining) : '—'}
        <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 6, color: '#6B7280' }}>remaining</span>
      </div>
      <div className="td-sla-bar">
        <div className="td-sla-bar-fill"
          style={{ width: `${progress}%`, background: isOverdue ? '#DC2626' : '#F59E0B' }} />
      </div>
      <div className="td-sla-meta-row">
        <span><span>Level:</span> {level}</span>
        <span><span>SLA:</span> {formatMins(total)}</span>
      </div>
      <div className="td-sla-meta-row">
        <span><span>Elapsed:</span> {formatMins(elapsed)}</span>
        <span><span>Remaining:</span> {formatMins(remaining)}</span>
      </div>
    </div>
  )
}

/* ── Quick Actions — status + comment → PUT update-status ── */
const STATUS_OPTION_VALUES = new Set(STATUS_OPTIONS.map(o => o.value))

function QuickActions({ status, ticketId, fetchJson, onStatusUpdated }) {
  const validStatus = STATUS_OPTION_VALUES.has(status) ? status : ''
  const [qaStatus, setQaStatus] = useState(validStatus)
  const [comment,  setComment]  = useState('')
  const [posting,  setPosting]  = useState(false)
  const [postError, setPostError] = useState('')

  useEffect(() => {
    setQaStatus(STATUS_OPTION_VALUES.has(status) ? status : '')
  }, [status])

  const handlePost = async () => {
    if (!comment.trim() || !qaStatus || !fetchJson || !ticketId) return
    setPosting(true)
    setPostError('')
    try {
      await fetchJson(`/api/tickets/ticket/update-status/${ticketId}/`, {
        method: 'PUT',
        body: JSON.stringify({ status: qaStatus, comment }),
      })
      onStatusUpdated(qaStatus)
      setComment('')
    } catch (err) {
      setPostError(err?.message || 'Failed to update status')
    } finally {
      setPosting(false)
    }
  }

  return (
    <div className="td-card td-card-sidebar td-qa-card">
      <div className="td-card-title">Quick Actions</div>
      <label className="td-qa-label" htmlFor="td-qa-status">Update Status</label>
      <select
        id="td-qa-status"
        className="td-qa-select"
        value={qaStatus}
        onChange={e => setQaStatus(e.target.value)}
      >
        <option value="" disabled>— Change Status —</option>
        {STATUS_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      <textarea
        className="td-qa-textarea"
        placeholder="Type comment..."
        value={comment}
        onChange={e => setComment(e.target.value)}
      />
      {postError && (
        <p style={{ fontSize: 11, color: '#DC2626', margin: '4px 0 0' }}>{postError}</p>
      )}
      <button
        type="button"
        className="td-qa-post"
        disabled={!comment.trim() || !qaStatus || posting}
        onClick={handlePost}
      >
        {posting ? 'Posting…' : 'Post'}
      </button>
    </div>
  )
}

function ReassignModal({ ticketId, fetchJson, onClose, onSuccess }) {
  const [mode,          setMode]          = useState('technician') // 'technician' | 'team'
  const [teams,         setTeams]         = useState([])
  const [teamsLoading,  setTeamsLoading]  = useState(false)
  const [selectedTeam,  setSelectedTeam]  = useState(null)   // {id, name, slug}
  const [cities,        setCities]        = useState([])
  const [citiesLoading, setCitiesLoading] = useState(false)  // loading first page
  const [citiesMore,    setCitiesMore]    = useState(false)  // has next page
  const [citiesPage,    setCitiesPage]    = useState(0)      // current loaded page
  const [citiesAppend,  setCitiesAppend]  = useState(false)  // loading next page
  const [cityInput,     setCityInput]     = useState('')     // text in search box
  const [selectedCity,  setSelectedCity]  = useState(null)   // {id, name, slug}
  const [cityOpen,      setCityOpen]      = useState(false)
  const [agents,        setAgents]        = useState([])
  const [agentsLoading, setAgentsLoading] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState('')
  const [submitting,    setSubmitting]    = useState(false)
  const [error,         setError]         = useState('')

  const cityWrapRef      = useRef(null)
  const debounceRef      = useRef(null)
  const loadingRef       = useRef(false)
  const currentQuery     = useRef('')        // tracks query in-flight for append calls
  const teamsFetchedRef  = useRef(false)     // fetch teams at most once per modal open
  const PAGE_SIZE        = 100

  // Switch between "ERT Technician" and "Assign Team" — start each flow fresh
  function handleModeChange(next) {
    if (next === mode) return
    setMode(next)
    setSelectedTeam(null)
    setSelectedAgent('')
    setAgents([])
    setSelectedCity(null)
    setCityInput('')
    setCities([])
    fetchCities(1, '', false)
  }

  // Fetch teams (once) when the "Assign Team" flow is opened
  useEffect(() => {
    if (mode !== 'team' || teamsFetchedRef.current) return
    teamsFetchedRef.current = true
    setTeamsLoading(true)
    fetchJson('/api/agents/teams')
      .then(data => setTeams(Array.isArray(data) ? data : (data?.teams || [])))
      .catch(() => setTeams([]))
      .finally(() => setTeamsLoading(false))
  }, [mode, fetchJson])

  // ── fetch a page of cities ──────────────────────────────────────────────────
  function fetchCities(page, query, append) {
    if (loadingRef.current && append) return   // prevent duplicate appends
    loadingRef.current = true
    currentQuery.current = query

    if (!append) setCitiesLoading(true)
    else setCitiesAppend(true)

    const qs = query.trim() ? `&search=${encodeURIComponent(query)}` : ''
    fetchJson(`/api/tickets/ert-ticket-manager/fetch-cities/?page=${page}&page_size=${PAGE_SIZE}${qs}`)
      .then(data => {
        const results = data?.data?.results || []
        setCities(prev => append ? [...prev, ...results] : results)
        setCitiesMore(!!data?.data?.next)
        setCitiesPage(page)
      })
      .catch(() => { if (!append) setCities([]) })
      .finally(() => {
        setCitiesLoading(false)
        setCitiesAppend(false)
        loadingRef.current = false
      })
  }

  // Initial load on mount
  useEffect(() => { fetchCities(1, '', false) }, []) // eslint-disable-line

  // Search input → debounce 400 ms → fetch page 1 from backend
  function handleCitySearch(val) {
    setCityInput(val)
    if (selectedCity) { setSelectedCity(null); setSelectedAgent('') }
    setCityOpen(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setCities([])
      fetchCities(1, val, false)
    }, 400)
  }

  // Infinite scroll — load next page when near bottom
  function handleListScroll(e) {
    const el = e.currentTarget
    if (!citiesMore || citiesAppend || loadingRef.current) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      fetchCities(citiesPage + 1, currentQuery.current, true)
    }
  }

  // Close dropdown on outside click
  useEffect(() => {
    function handle(e) {
      if (cityWrapRef.current && !cityWrapRef.current.contains(e.target)) setCityOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  // Fetch agents when city selected — in "Assign Team" mode, also require a team
  useEffect(() => {
    if (!selectedCity || (mode === 'team' && !selectedTeam)) { setAgents([]); return }
    setAgentsLoading(true)
    setSelectedAgent('')
    const teamParam = mode === 'team' && selectedTeam ? `&team=${selectedTeam.slug}` : ''
    fetchJson(`/api/tickets/ert-ticket-manager/agents/?city=${selectedCity.slug}${teamParam}`)
      .then(data => setAgents(data?.data?.results || data?.results || []))
      .catch(() => setAgents([]))
      .finally(() => setAgentsLoading(false))
  }, [selectedCity, selectedTeam, mode, fetchJson])

  const handleSubmit = async () => {
    if (!selectedAgent || submitting) return
    if (mode === 'team' && !selectedTeam) return
    setSubmitting(true)
    setError('')
    try {
      const body = { agent_id: selectedAgent }
      if (mode === 'team') body.team_id = selectedTeam.id
      await fetchJson(`/api/tickets/ert-ticket-manager/${ticketId}/assign/`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      onSuccess()
      onClose()
    } catch (err) {
      setError(err?.message || 'Failed to re-assign ticket.')
    } finally {
      setSubmitting(false)
    }
  }

  const selectStyle = (disabled) => ({
    width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB',
    borderRadius: 8, fontSize: 13, color: disabled ? '#9CA3AF' : '#111827',
    outline: 'none', background: disabled ? '#F9FAFB' : '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer', boxSizing: 'border-box',
  })

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 16px',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 440,
        boxShadow: '0 20px 60px rgba(0,0,0,.25)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #E5E7EB',
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Re-assign Technician</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#6B7280', lineHeight: 1, padding: 0 }}>×</button>
        </div>

        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── Mode switch ── */}
          <div style={{ display: 'flex', gap: 4, padding: 4, background: '#F3F4F6', borderRadius: 10 }}>
            <button
              type="button"
              onClick={() => handleModeChange('technician')}
              style={{
                flex: 1, padding: '8px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontSize: 12.5, fontWeight: 700,
                background: mode === 'technician' ? '#fff' : 'transparent',
                color: mode === 'technician' ? '#0090D9' : '#6B7280',
                boxShadow: mode === 'technician' ? '0 1px 3px rgba(0,0,0,.12)' : 'none',
              }}
            >
              ERT Technician
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('team')}
              style={{
                flex: 1, padding: '8px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontSize: 12.5, fontWeight: 700,
                background: mode === 'team' ? '#fff' : 'transparent',
                color: mode === 'team' ? '#0090D9' : '#6B7280',
                boxShadow: mode === 'team' ? '0 1px 3px rgba(0,0,0,.12)' : 'none',
              }}
            >
              Assign Team
            </button>
          </div>

          {/* ── Team ── (Assign Team flow only) */}
          {mode === 'team' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Team</label>
              <select
                value={selectedTeam?.id || ''}
                onChange={e => {
                  const t = teams.find(x => x.id === e.target.value)
                  setSelectedTeam(t || null)
                  setSelectedAgent('')
                }}
                disabled={teamsLoading}
                style={selectStyle(teamsLoading)}
              >
                <option value="">{teamsLoading ? 'Loading teams…' : '— Select Team —'}</option>
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* ── City — searchable + infinite scroll ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }} ref={cityWrapRef}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>City</label>
            <div style={{ position: 'relative' }}>

              {/* Input — read-only when city is selected; clears on × */}
              <input
                type="text"
                value={selectedCity ? selectedCity.name : cityInput}
                readOnly={!!selectedCity}
                onChange={e => handleCitySearch(e.target.value)}
                onFocus={() => { if (!selectedCity) setCityOpen(true) }}
                placeholder="Search city…"
                style={{
                  width: '100%', padding: '9px 36px 9px 12px',
                  border: `1px solid ${selectedCity ? '#0090D9' : '#D1D5DB'}`,
                  borderRadius: 8, fontSize: 13, color: '#111827',
                  outline: 'none', background: '#fff',
                  cursor: selectedCity ? 'default' : 'text',
                  boxSizing: 'border-box',
                }}
              />
              <span
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  color: '#9CA3AF', fontSize: selectedCity ? 16 : 12,
                  cursor: selectedCity ? 'pointer' : 'default', userSelect: 'none',
                }}
                onClick={() => {
                  if (selectedCity) {
                    setSelectedCity(null)
                    setCityInput('')
                    setSelectedAgent('')
                    setCityOpen(true)
                    fetchCities(1, '', false)
                  }
                }}
              >
                {selectedCity ? '×' : citiesLoading ? '…' : '▾'}
              </span>

              {/* Dropdown */}
              {cityOpen && !selectedCity && (
                <div
                  onScroll={handleListScroll}
                  style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200,
                    background: '#fff', border: '1px solid #D1D5DB', borderRadius: 8,
                    maxHeight: 220, overflowY: 'auto',
                    boxShadow: '0 6px 20px rgba(0,0,0,.12)',
                  }}
                >
                  {citiesLoading ? (
                    <div style={{ padding: '12px', fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>Searching…</div>
                  ) : cities.length === 0 ? (
                    <div style={{ padding: '12px', fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>No cities found</div>
                  ) : (
                    <>
                      {cities.map(c => (
                        <div
                          key={c.id}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => {
                            setSelectedCity({ id: c.id, name: c.name, slug: c.slug })
                            setCityOpen(false)
                            setSelectedAgent('')
                          }}
                          style={{
                            padding: '9px 12px', fontSize: 13, cursor: 'pointer',
                            color: '#111827', borderBottom: '1px solid #F3F4F6', background: '#fff',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F0F9FF'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                          {c.name}
                        </div>
                      ))}
                      {citiesAppend && (
                        <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>
                          Loading more…
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Technician ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Technician / Agent</label>
            <select
              value={selectedAgent}
              onChange={e => setSelectedAgent(e.target.value)}
              disabled={!selectedCity || (mode === 'team' && !selectedTeam) || agentsLoading}
              style={selectStyle(!selectedCity || (mode === 'team' && !selectedTeam) || agentsLoading)}
            >
              <option value="">{agentsLoading ? 'Loading agents…' : '— Select Technician —'}</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>
                  {[a.first_name, a.last_name].filter(Boolean).join(' ') || a.username || a.id}
                </option>
              ))}
            </select>
          </div>

          {error && <p style={{ fontSize: 12, color: '#DC2626', margin: 0 }}>{error}</p>}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 10,
          padding: '14px 20px', borderTop: '1px solid #E5E7EB',
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 18px', border: '1px solid #D1D5DB', borderRadius: 8,
              background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedAgent || (mode === 'team' && !selectedTeam) || submitting}
            style={{
              padding: '8px 18px', border: 'none', borderRadius: 8,
              background: !selectedAgent || (mode === 'team' && !selectedTeam) || submitting ? '#93C5FD' : '#0090D9',
              color: '#fff', fontWeight: 600, fontSize: 13,
              cursor: !selectedAgent || (mode === 'team' && !selectedTeam) || submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Assigning…' : 'Re-assign'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ══════════════════════════════════════
   ERT Ticket Closure Modal
   Mandatory closure form for ERT-team tickets — must be completed
   before an ERT ticket can be marked closed.
══════════════════════════════════════ */
const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB',
  borderRadius: 8, fontSize: 13.5, color: '#111827',
  outline: 'none', background: '#fff', boxSizing: 'border-box', fontFamily: 'inherit',
}
const selectFieldStyle = { ...inputStyle, appearance: 'auto', cursor: 'pointer' }
const labelStyle = { fontSize: 12.5, fontWeight: 600, color: '#374151' }

function ErtField({ name, label, required, error, children }) {
  return (
    <div
      data-field={name}
      style={{
        display: 'flex', flexDirection: 'column', gap: 6,
        ...(error ? { padding: '8px 10px', margin: '-8px -10px', background: '#FEF2F2', borderRadius: 10, border: '1px solid #FECACA' } : null),
      }}
    >
      <label style={labelStyle}>{label}{required && <span style={{ color: '#DC2626' }}> *</span>}</label>
      {children}
      {error && <span style={{ fontSize: 11.5, color: '#DC2626', fontWeight: 600 }}>⚠ {error}</span>}
    </div>
  )
}

function ErtRadioGroup({ value, onChange, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 24 }}>
        {['yes', 'no'].map(v => (
          <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5, color: '#111827', cursor: 'pointer' }}>
            <input type="radio" checked={value === v} onChange={() => onChange(v)}
              style={{ width: 16, height: 16, accentColor: '#0090D9', cursor: 'pointer' }} />
            {v === 'yes' ? 'Yes' : 'No'}
          </label>
        ))}
      </div>
      {error && <span style={{ fontSize: 11.5, color: '#DC2626' }}>{error}</span>}
    </div>
  )
}

const RESOLUTION_TYPES = [
  {
    value: 'physical',
    label: 'Physical Resolution',
    hint: 'Technician was physically on-site.',
  },
  {
    value: 'remote',
    label: 'Remote Resolution',
    hint: 'Issue resolved remotely.',
  },
]

const emptyErtDetails = (resolutionType) => ({
  resolutionType,
  actualFault: '', soc: '', bpCheckedAt: '', toolUsed: '',
  bpId: '', issueResolved: '', drillingRequired: '', remarks: '',
})

// Visual top-to-bottom order of fields, used to find the first invalid one to scroll to.
const ERT_FIELD_ORDER = [
  'actualFault', 'soc', 'bpCheckedAt', 'toolUsed', 'bpId',
  'issueResolved', 'drillingRequired', 'photos', 'remarks',
]

const ERT_FIELD_LABELS = {
  actualFault:      'Actual Fault Found',
  soc:               'SOC',
  bpCheckedAt:       'BP Checked At',
  toolUsed:          'Tool Used to Remove BP',
  bpId:              'BP ID',
  issueResolved:     'Issue Resolved',
  drillingRequired:  'Drilling Required',
  photos:            'Battery Photos',
  remarks:           'Remarks',
}

function ErtClosureModal({ onClose, onSubmit }) {
  const [step, setStep] = useState('type') // 'type' | 'details'
  const [form, setForm] = useState(emptyErtDetails(''))
  const [photos, setPhotos]     = useState([]) // [{file, preview}]
  const [errors, setErrors]     = useState({})
  const [faultQuery, setFaultQuery] = useState('')
  const [faultOpen, setFaultOpen]   = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [toasts, setToasts] = useState([])

  const faultWrapRef  = useRef(null)
  const photoInputRef = useRef(null)
  const fieldsWrapRef  = useRef(null)
  const toastTimers    = useRef({})

  useEffect(() => () => { Object.values(toastTimers.current).forEach(clearTimeout) }, [])

  function dismissToast(id) {
    clearTimeout(toastTimers.current[id])
    delete toastTimers.current[id]
    setToasts(t => t.filter(x => x.id !== id))
  }

  function showToast(msg) {
    const id = `${Date.now()}-${Math.random()}`
    setToasts(t => [...t, { id, msg }])
    toastTimers.current[id] = setTimeout(() => dismissToast(id), 5000)
  }

  const isPhysical = form.resolutionType === 'physical'
  const bdIdRequired = isPhysical && ERT_BD_ID_REQUIRED_TOOLS.includes(form.toolUsed)
  const faultOptions = isPhysical ? ERT_PHYSICAL_FAULT_OPTIONS : ERT_REMOTE_FAULT_OPTIONS

  useEffect(() => {
    function handle(e) {
      if (faultWrapRef.current && !faultWrapRef.current.contains(e.target)) setFaultOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
    setErrors(prev => ({ ...prev, [field]: '' }))
  }

  function selectResolutionType(value) {
    setForm(emptyErtDetails(value))
    setFaultQuery('')
    setPhotos([])
    setErrors({})
    setStep('details')
  }

  const filteredFaults = faultOptions.filter(f =>
    f.toLowerCase().includes(faultQuery.toLowerCase())
  )

  function handlePhotoSelect(e) {
    const selected = Array.from(e.target.files || [])
    if (!selected.length) return
    setPhotos(prev => [...prev, ...selected.map(file => ({ file, preview: URL.createObjectURL(file) }))])
    setErrors(prev => ({ ...prev, photos: '' }))
    if (photoInputRef.current) photoInputRef.current.value = ''
  }

  function removePhoto(i) {
    setPhotos(prev => prev.filter((_, idx) => idx !== i))
  }

  function validate() {
    const e = {}
    if (!form.actualFault)   e.actualFault   = 'Required'
    if (!form.issueResolved) e.issueResolved = 'Required'
    if (!form.remarks.trim()) e.remarks      = 'Required'

    if (isPhysical) {
      if (!form.soc)              e.soc              = 'Required'
      if (!form.bpCheckedAt)      e.bpCheckedAt      = 'Required'
      if (!form.toolUsed)         e.toolUsed         = 'Required'
      if (!form.drillingRequired) e.drillingRequired = 'Required'
      if (bdIdRequired && !form.bpId.trim()) e.bpId  = 'Required for the selected tool'
      else if (form.bpId.trim() && !/^[a-zA-Z0-9]{20}$/.test(form.bpId.trim()))
        e.bpId = 'BP ID must be 20 alphanumeric characters'
      if (!photos.length)         e.photos           = '1–4 photos or videos required'
      else if (photos.length > 4) e.photos           = 'Maximum 4 photos or videos'
    }
    return e
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const errs = validate()
    const errKeys = Object.keys(errs)
    if (errKeys.length) {
      setErrors(errs)
      const firstKey = ERT_FIELD_ORDER.find(k => errs[k])
      const rest = errKeys.length - 1
      showToast(
        `${ERT_FIELD_LABELS[firstKey] || firstKey}: ${errs[firstKey]}`
        + (rest > 0 ? ` (+${rest} more field${rest > 1 ? 's' : ''})` : '')
      )
      const el = fieldsWrapRef.current?.querySelector(`[data-field="${firstKey}"]`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setSubmitError('')
    setSubmitting(true)
    try {
      await onSubmit({
        resolutionType:    form.resolutionType,
        actualFault:       form.actualFault,
        soc:                isPhysical ? form.soc : '',
        bpCheckedAt:        isPhysical ? form.bpCheckedAt : '',
        toolUsed:           isPhysical ? form.toolUsed : '',
        bpId:               isPhysical ? form.bpId.trim() : '',
        issueResolved:      form.issueResolved,
        drillingRequired:   isPhysical ? form.drillingRequired : '',
        remarks:            form.remarks,
        photos:             photos.map(p => p.file),
      })
      onClose()
    } catch (err) {
      setSubmitError(err?.message || 'Something went wrong. Please retry.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 16px',
      }}
      onClick={e => { if (e.target === e.currentTarget && !submitting) onClose() }}
    >
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460,
        maxHeight: 'calc(100vh - 48px)',
        boxShadow: '0 20px 60px rgba(0,0,0,.25)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
          padding: '16px 20px', borderBottom: '1px solid #E5E7EB',
        }}>
          <button
            type="button"
            onClick={() => { if (submitting) return; step === 'details' ? setStep('type') : onClose() }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#374151', display: 'flex' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Ticket Closure</div>
          {step === 'details' && (
            <span style={{
              marginLeft: 'auto', fontSize: 11, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase',
              color: '#0090D9', background: '#F0F9FF', border: '1px solid #BAE6FD',
              borderRadius: 20, padding: '3px 10px',
            }}>
              {isPhysical ? 'Physical' : 'Remote'}
            </span>
          )}
        </div>

        <div style={{
          padding: '8px 20px 4px', fontSize: 12, color: '#92400E', flexShrink: 0,
          background: '#FFFBEB', borderBottom: '1px solid #FDE68A',
        }}>
          This form is mandatory for ERT tickets before closing.
        </div>

        {step === 'type' ? (
          /* ── Step 1 — Resolution type ── */
          <div style={{ padding: '20px 20px 22px', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', minHeight: 0 }}>
            <div style={{ fontSize: 12.5, color: '#6B7280' }}>How was this ticket resolved?</div>
            {RESOLUTION_TYPES.map(rt => (
              <button
                key={rt.value}
                type="button"
                onClick={() => selectResolutionType(rt.value)}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left',
                  padding: '14px 16px', border: '1.5px solid #E5E7EB', borderRadius: 10,
                  background: '#fff', cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#0090D9'; e.currentTarget.style.background = '#F0F9FF' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = '#fff' }}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{rt.label}</span>
                <span style={{ fontSize: 12.5, color: '#6B7280' }}>{rt.hint}</span>
              </button>
            ))}
          </div>
        ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div ref={fieldsWrapRef} style={{ overflowY: 'auto', minHeight: 0, padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Actual Fault Found — searchable select */}
          <ErtField name="actualFault" label="Actual Fault Found" required error={errors.actualFault}>
            <div style={{ position: 'relative' }} ref={faultWrapRef}>
              <input
                type="text"
                value={form.actualFault ? form.actualFault : faultQuery}
                onFocus={() => { if (form.actualFault) { set('actualFault', ''); setFaultQuery('') } setFaultOpen(true) }}
                onChange={e => { setFaultQuery(e.target.value); setFaultOpen(true) }}
                placeholder="Search fault…"
                style={{ ...inputStyle, paddingRight: 32 }}
              />
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', pointerEvents: 'none' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
              </span>
              {faultOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200,
                  background: '#fff', border: '1px solid #D1D5DB', borderRadius: 8,
                  maxHeight: 200, overflowY: 'auto', boxShadow: '0 6px 20px rgba(0,0,0,.12)',
                }}>
                  {filteredFaults.length === 0 ? (
                    <div style={{ padding: 12, fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>No results</div>
                  ) : filteredFaults.map(f => (
                    <div key={f}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => { set('actualFault', f); setFaultQuery(''); setFaultOpen(false) }}
                      style={{ padding: '9px 12px', fontSize: 13, cursor: 'pointer', color: '#111827', borderBottom: '1px solid #F3F4F6' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F0F9FF'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      {f}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ErtField>

          {isPhysical && (
            <>
              {/* SOC */}
              <ErtField name="soc" label="SOC" required error={errors.soc}>
                <select value={form.soc} onChange={e => set('soc', e.target.value)} style={selectFieldStyle}>
                  <option value="">Select</option>
                  {ERT_SOC_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </ErtField>

              {/* BP Checked At */}
              <ErtField name="bpCheckedAt" label="BP Checked At" required error={errors.bpCheckedAt}>
                <select value={form.bpCheckedAt} onChange={e => set('bpCheckedAt', e.target.value)} style={selectFieldStyle}>
                  <option value="">Select</option>
                  {ERT_BP_CHECKED_AT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </ErtField>

              {/* Tool Used to Remove BP */}
              <ErtField name="toolUsed" label="Tool Used to Remove BP" required error={errors.toolUsed}>
                <select
                  value={form.toolUsed}
                  onChange={e => set('toolUsed', e.target.value)}
                  style={selectFieldStyle}
                >
                  <option value="">Select</option>
                  {ERT_TOOL_USED_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </ErtField>

              {/* BP ID — required only for specific tools */}
              <ErtField name="bpId" label={`BP ID${bdIdRequired ? '' : ' (Optional)'}`} required={bdIdRequired} error={errors.bpId}>
                <input
                  type="text"
                  value={form.bpId}
                  onChange={e => set('bpId', e.target.value)}
                  placeholder="Enter BP ID"
                  maxLength={20}
                  style={inputStyle}
                />
                {!errors.bpId && (
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>If entered, must be exactly 20 alphanumeric characters</span>
                )}
                <button
                  type="button"
                  title="QR scanning will be enabled once camera integration is wired up"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    width: '100%', padding: '10px 14px', marginTop: 6,
                    background: '#F3F4F6', color: '#9CA3AF', border: '1px dashed #D1D5DB',
                    borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'not-allowed',
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><path d="M14 14h3v3h-3zM21 14v7M18 21h3M14 21h.01" /></svg>
                  Scan BP QR Code (coming soon)
                </button>
              </ErtField>
            </>
          )}

          {/* Issue Resolved */}
          <ErtField name="issueResolved" label="Issue Resolved" required error={errors.issueResolved}>
            <ErtRadioGroup value={form.issueResolved} onChange={v => set('issueResolved', v)} />
          </ErtField>

          {isPhysical && (
            <>
              {/* Drilling Required */}
              <ErtField name="drillingRequired" label="Drilling Required" required error={errors.drillingRequired}>
                <ErtRadioGroup value={form.drillingRequired} onChange={v => set('drillingRequired', v)} />
              </ErtField>

              {/* Battery Photos — required, 1–4 photos or videos */}
              <ErtField name="photos" label="Battery Photos" required error={errors.photos}>
                <input
                  ref={photoInputRef} type="file" accept="image/*,video/*" multiple capture="environment"
                  style={{ display: 'none' }} onChange={handlePhotoSelect}
                />
                {photos.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {photos.map((p, i) => (
                      <div key={i} style={{ position: 'relative', width: 64, height: 64 }}>
                        {p.file.type.startsWith('video') ? (
                          <video src={p.preview} muted style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E7EB' }} />
                        ) : (
                          <img src={p.preview} alt={`battery-${i}`} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E7EB' }} />
                        )}
                        <button type="button" onClick={() => removePhoto(i)}
                          style={{
                            position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%',
                            background: '#DC2626', color: '#fff', border: '2px solid #fff', fontSize: 11, lineHeight: 1,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                          }}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}
                {photos.length < 4 && (
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                      width: 84, height: 64, background: '#F0F9FF', border: '1.5px dashed #BAE6FD',
                      borderRadius: 8, color: '#0090D9', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                    Add Photo
                  </button>
                )}
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>1–4 photos or videos</span>
              </ErtField>
            </>
          )}

          {/* Remarks */}
          <ErtField name="remarks" label="Remarks" required error={errors.remarks}>
            <textarea
              value={form.remarks}
              onChange={e => set('remarks', e.target.value)}
              placeholder="Enter remarks"
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 90, lineHeight: 1.5 }}
            />
          </ErtField>

          {submitError && (
            <div style={{ fontSize: 12.5, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '9px 12px' }}>
              {submitError}
            </div>
          )}
        </div>

          {/* Footer — stays pinned below the scrolling fields, always visible */}
          <div style={{ display: 'flex', gap: 10, flexShrink: 0, padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
            <button
              type="button"
              onClick={() => !submitting && onClose()}
              disabled={submitting}
              style={{
                flex: 1, padding: '11px 16px', border: '1px solid #D1D5DB', borderRadius: 9,
                background: '#fff', color: '#374151', fontWeight: 700, fontSize: 13.5,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                flex: 1, padding: '11px 16px', border: 'none', borderRadius: 9,
                background: submitting ? '#93C5FD' : '#0090D9', color: '#fff', fontWeight: 700, fontSize: 13.5,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </form>
        )}
      </div>

      {/* ── Toast notifications ── */}
      {toasts.length > 0 && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10050, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
          {toasts.map(t => (
            <div key={t.id} style={{
              pointerEvents: 'all',
              minWidth: 280, maxWidth: 380,
              background: '#FFF1F1', border: '1px solid #FECACA', borderLeft: '4px solid #EF4444',
              borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.18)',
              padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <div style={{ fontSize: 18, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>⚠️</div>
              <div style={{ flex: 1, fontSize: 12.5, color: '#991B1B', lineHeight: 1.4 }}>{t.msg}</div>
              <button
                type="button"
                onClick={() => dismissToast(t.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991B1B', fontSize: 15, lineHeight: 1, padding: 0, flexShrink: 0 }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function TicketDetail({ ticket, setCurrentPage, fetchJson, setSelectedVehicle, currentUser }) {
  const [detail,            setDetail]           = useState(null)
  const [loading,           setLoading]          = useState(true)
  const [error,             setError]            = useState(null)
  const [activeTab,         setActiveTab]        = useState(getInitialTabFromUrl)
  const [followupLogged,    setFollowupLogged]   = useState(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).get('followupLogged') === '1'
  })
  const [statusValue,       setStatusValue]      = useState('in_progress')
  const [statusUpdating,    setStatusUpdating]   = useState(false)
  const [detailRefresh,     setDetailRefresh]    = useState(0)
  const [attachments,         setAttachments]        = useState([])
  const [attachmentsLoading,  setAttachmentsLoading] = useState(false)
  const [evidenceFiles,       setEvidenceFiles]      = useState([])
  const [evidenceUploading,   setEvidenceUploading]  = useState(false)
  const [evidenceUploadError, setEvidenceUploadError] = useState('')
  const [evidenceRefresh,     setEvidenceRefresh]    = useState(0)
  const evidenceFileInputRef = useRef(null)
  const [resolutionAttachments,        setResolutionAttachments]        = useState([])
  const [resolutionAttachmentsLoading, setResolutionAttachmentsLoading] = useState(false)
  const [resolutionOpen,               setResolutionOpen]               = useState(false)
  const [closureOpen,                  setClosureOpen]                  = useState(false)
  const [editOpen,                     setEditOpen]                     = useState(false)
  const [reassignOpen,                 setReassignOpen]                 = useState(false)
  const [ertClosureOpen,               setErtClosureOpen]               = useState(false)
  const [viewerSrc,                    setViewerSrc]                    = useState(null)
  const [locationData,                 setLocationData]                 = useState(null)
  const [locationLoading,              setLocationLoading]              = useState(false)
  const [locationError,                setLocationError]                = useState(null)

  const isErtUser = Array.isArray(currentUser?.feature_access) && currentUser.feature_access.includes('ert_page')

  const ticketId = ticket?.ticket_id ?? getTicketIdFromUrl()

  /* ── Remote Battery Unlock: checklist answers decide whether the tab is shown ── */
  const [checklist,        setChecklist]        = useState(null) // { stages, byStage, isChecklistTicket }
  const [checklistLoading, setChecklistLoading] = useState(false)
  const [checklistError,   setChecklistError]   = useState('')
  const [checklistRefresh, setChecklistRefresh] = useState(0)
  useEffect(() => {
    if (!ticketId || !fetchJson) return
    let active = true
    setChecklistLoading(true); setChecklistError('')
    fetchTicketAnswers(fetchJson, ticketId)
      .then(data => { if (active) setChecklist(data) })
      .catch(err => {
        if (!active) return
        setChecklist(null)
        setChecklistError(err?.status === 404 ? '' : (err?.message || 'Failed to load checklist answers.'))
      })
      .finally(() => { if (active) setChecklistLoading(false) })
    return () => { active = false }
  }, [ticketId, fetchJson, checklistRefresh])
  const remoteSessionLevels = useRemoteSessionLevels(fetchJson, currentUser)
  const checklistByStage = checklist?.byStage || {}
  const hasL1Checklist = !!checklist?.isChecklistTicket &&
    Object.values(checklistByStage).some(list => (list || []).length > 0)
  // red dot: the next unfilled stage is one this agent can fill
  const nextRemoteStage = (checklist?.stages || []).find(s => !(checklistByStage[s] || []).length)
  const ticketClosed = statusValue === 'closed'
  const remoteSessionPending = !ticketClosed && hasL1Checklist && !!nextRemoteStage && !!remoteSessionLevels?.includes(nextRemoteStage)
  const visibleTabs = TABS.filter(t => t.id !== REMOTE_SESSION_TAB || hasL1Checklist || activeTab === REMOTE_SESSION_TAB)

  /* sync ticket ID into URL */
  useEffect(() => {
    if (!ticket?.ticket_id || typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (url.searchParams.get(TICKET_ID_PARAM) !== String(ticket.ticket_id)) {
      url.searchParams.set(TICKET_ID_PARAM, String(ticket.ticket_id))
      window.history.replaceState({}, '', url.toString())
    }
  }, [ticket?.ticket_id])

  /* strip one-shot `tab` / `followupLogged` params from the URL after reading them */
  useEffect(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (!url.searchParams.has('tab') && !url.searchParams.has('followupLogged')) return
    url.searchParams.delete('tab')
    url.searchParams.delete('followupLogged')
    window.history.replaceState({}, '', url.toString())
  }, [])

  /* fetch ticket detail */
  useEffect(() => {
    if (!ticketId) { setLoading(false); setError('Ticket not found.'); return }
    if (!fetchJson) return
    let active = true
    setLoading(true); setError(null); setDetail(null)
    fetchJson(`/api/tickets/ticket/${ticketId}/`)
      .then(data => {
        if (!active) return
        setDetail(data)
        const s = data?.ticket_information?.status
        if (s) setStatusValue(s.toLowerCase().replace(/[\s-]+/g, '_'))
      })
      .catch(err => {
        if (!active) return
        setError(err?.status === 403 || err?.status === 404
          ? "You don't have access to this ticket."
          : 'Failed to load ticket details.')
      })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [ticketId, fetchJson, detailRefresh])

  const info    = detail?.ticket_information || {}
  const rider   = detail?.rider_details      || {}
  const vehicle = detail?.vehicle_details    || {}
  const tat     = detail?.tat_details        || null
  const latestSwap = detail?.extra_details?.vehicle_latest_swap || null
  // All swap attempts captured at ticket creation (swap-details API → create-ticket payload).
  // Accept it at the top level or under extra_details, as a list or a single object.
  const swapDetails = (() => {
    const raw = detail?.swap_details ?? detail?.extra_details?.swap_details ?? null
    return Array.isArray(raw) ? raw : (raw ? [raw] : [])
  })()
  const timelines = detail?.ticket_timelines || []
  const communications = detail?.ticket_communications || []
  const resolutionData  = detail?.resolution_data  || null
  // Resolution details were already captured for this ticket (e.g. on a prior
  // closure attempt) — skip the ERT closure form and close the ticket directly.
  const hasResolutionData = !!(resolutionData && Object.keys(resolutionData).length > 0)
  const closureData     = info?.closure_data       || null
  const ticketEvidence  = detail?.ticket_evidence  || []
  const resolutionTeam  = detail?.team || null
  // Follow-ups, most recently created first. Tries to parse a real timestamp
  // from each entry (created_at/timestamp, or date+time combined); if none
  // of them parse, falls back to reversing the array (API assumed to return
  // oldest-first in that case).
  const followups = useMemo(() => {
    const raw = Array.isArray(detail?.followups)
      ? detail.followups
      : Object.values(detail?.followups || {})
    const withTime = raw.map((f, idx) => {
      const candidates = [
        f.created_at, f.timestamp,
        f.date && f.time ? `${f.date} ${f.time}` : null,
        f.date,
      ].filter(Boolean)
      let ts = null
      for (const c of candidates) {
        const d = new Date(c)
        if (!isNaN(d)) { ts = d.getTime(); break }
      }
      return { f, idx, ts }
    })
    const allParsed = withTime.length > 0 && withTime.every(x => x.ts !== null)
    if (allParsed) {
      return withTime.sort((a, b) => b.ts - a.ts).map(x => x.f)
    }
    return [...raw].reverse()
  }, [detail?.followups])

  /* fetch attachments when Evidence tab opens or after upload */
  useEffect(() => {
    if (activeTab !== 'evidence' || !ticketId || !fetchJson) return
    setAttachmentsLoading(true)
    fetchJson(`/api/tickets/ticket/get-attachments/${ticketId}/?attachment_type=create_ticket`)
      .then(data => setAttachments(Array.isArray(data) ? data : []))
      .catch(() => setAttachments([]))
      .finally(() => setAttachmentsLoading(false))
  }, [activeTab, ticketId, fetchJson, evidenceRefresh])

  /* fetch location when Location tab opens */
  useEffect(() => {
    if (activeTab !== 'location' || !ticketId || !fetchJson) return
    setLocationLoading(true)
    setLocationError(null)
    fetchJson(`/api/tickets/ticket/ticket-location/${ticketId}/`)
      .then(data => setLocationData(Array.isArray(data) ? data : (data ? [data] : [])))
      .catch(err => setLocationError(err?.message || 'Failed to load location.'))
      .finally(() => setLocationLoading(false))
  }, [activeTab, ticketId, fetchJson])

  /* fetch resolution attachments when resolution panel opens */
  useEffect(() => {
    if (!resolutionOpen || !ticketId || !fetchJson) return
    const attachmentType = resolutionData?.attachment_type
    if (!attachmentType) { setResolutionAttachments([]); return }
    setResolutionAttachmentsLoading(true)
    fetchJson(`/api/tickets/ticket/get-attachments/${ticketId}/?attachment_type=${attachmentType}`)
      .then(data => setResolutionAttachments(Array.isArray(data) ? data : []))
      .catch(() => setResolutionAttachments([]))
      .finally(() => setResolutionAttachmentsLoading(false))
  }, [resolutionOpen, ticketId, fetchJson, resolutionData?.attachment_type])

  /* header dropdown → PUT status only (no comment) */
  const handleHeaderStatusChange = async (newStatus) => {
    if (!fetchJson || !ticketId || statusUpdating) return
    setStatusUpdating(true)
    try {
      await fetchJson(`/api/tickets/ticket/update-status/${ticketId}/`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      })
      setStatusValue(newStatus)
      setDetailRefresh(n => n + 1)
    } catch { }
    finally { setStatusUpdating(false) }
  }

  // Ticket already has resolution_data captured (non-empty) — close it straight
  // away without reopening the ERT closure form to collect those details again.
  const handleDirectClose = async () => {
    if (!fetchJson || !ticketId || statusUpdating) return
    setStatusUpdating(true)
    try {
      await fetchJson(`/api/tickets/ticket/update-status/${ticketId}/`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'closed' }),
      })
      setStatusValue('closed')
      setDetailRefresh(n => n + 1)
    } catch { }
    finally { setStatusUpdating(false) }
  }

  // ERT closure form submit — mirrors ert_ticket_closure_flow.md exactly:
  // 1) upload attachments (stop on failure), 2) update ticket status with the
  // resolution_type-specific payload shape.
  const handleErtClosureSubmit = async (closureFields) => {
    const {
      resolutionType, actualFault, soc, bpCheckedAt, toolUsed,
      bpId, issueResolved, drillingRequired, remarks, photos,
    } = closureFields

    // 1. Upload attachments first — if this fails, stop and surface the error;
    //    do not proceed to the status update.
    if (photos && photos.length) {
      // Same route Create Ticket / Evidence uploads use — just a different attachment_type.
      const fd = new FormData()
      photos.forEach((file) => fd.append('files', file))
      await fetchJson(
        `/api/tickets/ticket/add-attachments/${ticketId}/?attachment_type=resolve_ticket`,
        { method: 'POST', body: fd }
      )
    }

    // 2. Update ticket status
    const operatorName  = currentUser?.full_name || ''
    const operatorPhone = currentUser?.phone_number || currentUser?.mobile_number || currentUser?.phone || ''
    const resolvedBool  = issueResolved === 'yes'

    const closureData = resolutionType === 'physical'
      ? {
          resolution_type:         'physical',
          actual_fault_found:      actualFault,
          bp_soc:                  soc,
          bd_id:                   bpId || '',
          bp_checked_at:           bpCheckedAt,
          tool_used_to_remove_bp:  toolUsed,
          issue_resolved:          resolvedBool,
          drilling_required:       drillingRequired === 'yes',
          remarks,
          closed_by_name:          operatorName,
          closed_by_phone_number:  operatorPhone,
        }
      : {
          resolution_type:         'remote',
          actual_fault_found:      actualFault,
          issue_resolved:          resolvedBool,
          remarks,
          closed_by_name:          operatorName,
          closed_by_phone_number:  operatorPhone,
        }

    // Confirmed working route: /api/tickets/ticket/update-status/{ticketId}/ (PUT),
    // same one used by Quick Actions / the header status dropdown elsewhere in this
    // file — just with the ERT closure fields folded into the body alongside `status`.
    await fetchJson(`/api/tickets/ticket/update-status/${ticketId}/`, {
      method: 'PUT',
      body: JSON.stringify({
        status:       'closed',
        description:  remarks,
        issue_resolved: resolvedBool ? 'yes' : 'no',
        battery_id:   resolutionType === 'physical' && bpId ? bpId : null,
        closure_data: closureData,
        resolution_data: {
          issue_resolved: resolvedBool ? 'yes' : 'no',
          status:         'closed',
        },
      }),
    })

    setStatusValue('closed')
    setDetailRefresh(n => n + 1)
  }

  const ticketNumber = info.ticket_number || ticket?.ticket_number
  const vin = vehicle.vin_number || ticket?.vin_number
  const subtitle = useMemo(() => {
    const parts = []
    if (info.sub_category || info.category) parts.push(info.sub_category || info.category)
    if (vin) parts.push(`VIN: ${vin}`)
    return parts.join(' — ')
  }, [info.sub_category, info.category, vin])

  const confidence = formatConfidence(rider.identity_confidence)
  const theftRisk  = formatRisk(rider.theft_risk)
  const copyVin    = () => vin && navigator.clipboard?.writeText(vin)
  const goToVehicle360 = () => {
    if (!vin || !setSelectedVehicle || !setCurrentPage) return
    fetchJson(`/api/vehicles/search-bar?q=${encodeURIComponent(vin)}`)
      .then(data => {
        const v = data?.vehicle_details
        setSelectedVehicle({
          vehicle_id: v?.id || '',
          vin_number: v?.vin || vin,
          registration_number: v?.registration_number || vehicle.registration_number || '',
        })
        setCurrentPage('vehicle-360')
      })
      .catch(() => {
        setSelectedVehicle({ vehicle_id: '', vin_number: vin, registration_number: vehicle.registration_number || '' })
        setCurrentPage('vehicle-360')
      })
  }

  if (loading) return <div className="td-page td-feedback">Loading ticket details…</div>

  if (error || !detail) {
    return (
      <div className="td-page td-feedback error">
        {error || 'Failed to load ticket details.'}
        <div style={{ marginTop: 12 }}>
          <button type="button" className="td-btn td-btn-ghost" onClick={() => setCurrentPage('tickets')}>
            Back to tickets
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="td-page">
      <nav className="td-breadcrumb" aria-label="Breadcrumb">
        <button type="button" onClick={() => setCurrentPage('tickets')}>Tickets</button>
        <span className="td-breadcrumb-sep">›</span>
        <span className="td-breadcrumb-current">{ticketNumber}</span>
      </nav>

      {followupLogged && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8,
          padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#92400E',
        }}>
          <span>⚠</span>
          <span>
            Call from {rider.name || 'this rider'} {rider.number ? `(${rider.number})` : ''} logged as follow-up. No new ticket created.
          </span>
          <button
            type="button"
            onClick={() => setFollowupLogged(false)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#92400E', fontSize: 16, lineHeight: 1 }}
          >
            ×
          </button>
        </div>
      )}

      <div className="td-header-box">
        <div className="td-header-box-left">
          <div className="td-header-top-line">
            <h1 className="td-title">{ticketNumber}</h1>
            <div className="td-badges">
              {statusValue && (
                <span className={`td-badge status ${statusClass(statusValue)}`}>
                  <span className="td-badge-dot" />
                  {displayStatus(statusValue)}
                </span>
              )}
              {(info.call_stage || detail?.call_stage) && (
                <span className={`td-badge call-stage ${String(info.call_stage || detail?.call_stage).toLowerCase()}`} title="Call stage">
                  Call Stage: {String(info.call_stage || detail?.call_stage).toUpperCase()}
                </span>
              )}
              {info.priority && (
                <span className={`td-badge priority ${info.priority}`}>{displayPriority(info.priority)}</span>
              )}
              {info.ticket_stage && (
                <span className="td-badge stage">{String(info.ticket_stage).toUpperCase()}</span>
              )}
            </div>
          </div>
          <p className="td-subtitle">{subtitle}</p>
        </div>

        <div className="td-header-box-actions">
          <button type="button" className="td-btn td-btn-outline-blue" style={{ display: 'none' }}>
            <img src={phoneIcon} alt="" /> Call Rider
          </button>
          <button type="button" className="td-btn td-btn-outline-green" style={{ display: 'none' }}>
            <img src={messageIcon} alt="" /> WhatsApp
          </button>
          {statusValue?.toLowerCase() === 'opened' && (
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', border: '1px solid #D1D5DB', borderRadius: 7,
              background: '#fff', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, color: '#374151',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit
          </button>
          )}
          {isErtUser ? (
            statusValue !== 'closed' && (
              <button
                type="button"
                onClick={() => hasResolutionData ? handleDirectClose() : setErtClosureOpen(true)}
                disabled={statusUpdating}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', border: '1.5px solid #DC2626', borderRadius: 8,
                  background: '#FEF2F2', color: '#DC2626', fontWeight: 700, fontSize: 13,
                  cursor: statusUpdating ? 'not-allowed' : 'pointer',
                  opacity: statusUpdating ? 0.6 : 1,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
                {statusUpdating ? 'Closing…' : 'Close Ticket'}
              </button>
            )
          ) : (
            <div className="td-status-select-wrap">
              <select
                className="td-status-select"
                value={STATUS_OPTION_VALUES.has(statusValue) ? statusValue : ''}
                onChange={e => handleHeaderStatusChange(e.target.value)}
                disabled={statusUpdating}
                aria-label="Ticket status"
              >
                <option value="" disabled>Select to change status</option>
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <span className="td-status-select-arrow">{statusUpdating ? '…' : '▼'}</span>
            </div>
          )}
          <button type="button" className="td-btn td-btn-ghost" style={{ display: 'none' }}>Reassign</button>
        </div>
      </div>

      <div className="td-body-layout">
        <div className="td-body-main">
          <div className="td-tabs" role="tablist">
            {visibleTabs.map(tab => (
              <button
                key={tab.id} type="button" role="tab"
                aria-selected={activeTab === tab.id}
                className={`td-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
                {tab.id === REMOTE_SESSION_TAB && remoteSessionPending && (
                  <span aria-label="Pending" style={{
                    marginLeft: 6, width: 7, height: 7, borderRadius: '50%',
                    background: '#EF4444', display: 'inline-block', verticalAlign: 'middle',
                  }} />
                )}
                {tab.id === 'followups' && followups.length > 0 && (
                  <span style={{
                    marginLeft: 6, minWidth: 16, height: 16, padding: '0 4px',
                    borderRadius: 9, background: '#EF4444', color: '#fff',
                    fontSize: 10, fontWeight: 700, display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    {followups.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── Overview ── */}
          {activeTab === 'overview' && (
            <div className="td-main">
              <SectionCard title="Ticket Information">
                <InfoRow label="Ticket ID" value={ticketNumber} />
                <InfoRow label="Status">
                  <span className={`td-status-inline td-badge status ${statusClass(statusValue)}`}>
                    <span className="td-badge-dot" />{displayStatus(statusValue)}
                  </span>
                </InfoRow>
                <InfoRow label="Priority">
                  {info.priority
                    ? <span className={`td-priority-inline ${info.priority}`}>{displayPriority(info.priority)}</span>
                    : '—'}
                </InfoRow>
                <InfoRow label="Category"    value={info.category} />
                <InfoRow label="Sub-Category" value={info.sub_category} />
                <InfoRow label="CNC transaction id / CNC error code"
                  value={info.cnc_transaction_id || info.cnc_error_code
                    ? [info.cnc_transaction_id, info.cnc_error_code].filter(Boolean).join(' / ')
                    : '—'}
                />
                <InfoRow label="Auto-populated CNC transaction id / CNC error code"
                  value={latestSwap?.transaction_id || latestSwap?.error_code
                    ? [latestSwap?.transaction_id, latestSwap?.error_code].filter(Boolean).join(' / ')
                    : '—'}
                />
                <InfoRow label="Description" value={info.description} />
                <InfoRow label="Channel"     value={displayChannel(info.channel)} />
                <InfoRow label="Station ID"    value={info.station_code || info.station_id} />
                <InfoRow label="City"          value={info.city_name} />
                <InfoRow label="Region"        value={info.region_name} />
                <InfoRow label="Area/Locality" value={info.incident_area} />
                <InfoRow label="Ticket Created Date"
                  value={info.created_time && info.ticket_created_date
                    ? `${info.ticket_created_date} · ${info.created_time}`
                    : info.ticket_created_date || info.created_time}
                />
                <InfoRow label="Assigned To"      value={info.assigned_to} />
                <InfoRow label="Ticket raised by" value={info.ticket_raised_by || info.created_by} />
                <InfoRow label="Ticket stage"
                  value={info.ticket_stage ? String(info.ticket_stage).toUpperCase() : null} />
                <InfoRow label="Closing SLA"      value={info.closing_tat} />
                <InfoRow label="Type of Ticket"   value={info.ticket_type ? String(info.ticket_type).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null} />
                <InfoRow label="Location Type"    value={info.location_type ? String(info.location_type).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null} />
                <InfoRow label="Dock ID"          value={info.dock_id} />
                <InfoRow label="Changed Assigned Coordinator" value={info.changed_assigned_coordinator} />
                <InfoRow label="No. of Follow Up Tickets" value={info.follow_up_count ?? info.no_of_follow_up_tickets ?? null} />
              </SectionCard>

              <SectionCard title="Rider Details">
                <InfoRow label="Rider Name"   value={rider.name} />
                <InfoRow label="Rider Mobile" value={rider.number} />
                <InfoRow label="Email"        value={rider.email} />
                <InfoRow label="Preferred Channel" value={displayChannel(rider.preferred_channel)} />
                <InfoRow label="Language"     value={rider.language} />
                <InfoRow label="Customer Type" value={rider.customer_type || info.customer_type} />
              </SectionCard>

              <SectionCard title="Vehicle Details">
                <div className="td-row">
                  <span className="td-row-label">VIN</span>
                  <span className="td-row-value">
                    {vin ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <button type="button" className="td-vin-link" onClick={goToVehicle360} title="Open Vehicle 360">
                          {vin}
                        </button>
                        <button type="button" className="td-copy-icon" onClick={copyVin} title="Copy VIN" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>⧉</button>
                      </span>
                    ) : '—'}
                  </span>
                </div>
                <InfoRow label="Reg No."        value={vehicle.registration_number} />
                <InfoRow label="Type of Vehicle" value={vehicle.type || vehicle.vehicle_type || vehicle.type_of_vehicle} />
                <InfoRow label="OEM"            value={vehicle.oem} />
                <InfoRow label="Service Provider" value={vehicle.service_provider} />
                <InfoRow label="Vehicle status">
                  {vehicle.vehicle_status ? (
                    <span className="td-vehicle-status">
                      <span className="td-vehicle-status-dot" />{vehicle.vehicle_status}
                    </span>
                  ) : '—'}
                </InfoRow>
                <InfoRow label="Current Battery ID" value={vehicle.current_battery_id} />
                <InfoRow label="Vehicle Age" value={formatExperience(rider.vehicle_age)} />
              </SectionCard>
            </div>
          )}

          {/* ── Timeline ── */}
          {activeTab === 'timeline' && (
            <div style={{ padding: '20px 4px' }}>
              {timelines.length === 0 ? (
                <div className="td-tab-placeholder">No timeline events yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {timelines.flatMap((event, i) => {
                    const diff = i < timelines.length - 1
                      ? timeDiff(event.time, timelines[i + 1].time)
                      : null

                    const stepEl = (
                      <div key={`step-${i}`} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                        <div style={{ width: 16, flexShrink: 0, marginTop: 2,
                          borderRadius: '50%', height: 16,
                          background: timelineColor(event.title),
                          boxShadow: `0 0 0 3px ${timelineColor(event.title)}22`,
                        }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#0F172A', marginBottom: 2 }}>{event.title || '—'}</div>
                          {event.description && <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 4 }}>{event.description}</div>}
                          {event.time && <div style={{ fontSize: 12, color: '#94A3B8' }}>{event.time}</div>}
                        </div>
                      </div>
                    )

                    if (i >= timelines.length - 1) return [stepEl]

                    const gapEl = (
                      <div key={`gap-${i}`} style={{ display: 'flex', gap: 16, alignItems: 'center', margin: '6px 0' }}>
                        <div style={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                          <div style={{ width: 2, height: 28, background: '#E2E8F0' }} />
                        </div>
                        {diff && (
                          <span style={{
                            fontSize: 11, fontWeight: 600, color: '#64748B',
                            background: '#F1F5F9', border: '1px solid #CBD5E1',
                            padding: '3px 10px', borderRadius: 20,
                          }}>
                            {diff}
                          </span>
                        )}
                      </div>
                    )

                    return [stepEl, gapEl]
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Communications ── */}
          {activeTab === 'communications' && (
            <div style={{ padding: '20px 4px' }}>
              {communications.length === 0 ? (
                <div className="td-tab-placeholder">No communications yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {communications.map((comm, i) => {
                    const title = comm.title || ''
                    const isCall = title.toLowerCase().includes('call')
                    const isWhatsApp = title.toLowerCase().includes('whatsapp')
                    const isEmail = title.toLowerCase().includes('email')
                    const icon = isCall ? '📞' : isWhatsApp ? '💬' : isEmail ? '✉️' : '📱'

                    const statusColor =
                      (comm.status || '').toLowerCase() === 'delivered' ? '#16A34A' :
                      (comm.status || '').toLowerCase() === 'failed'    ? '#DC2626' :
                      (comm.status || '').toLowerCase() === 'pending'   ? '#D97706' :
                      '#6B7280'

                    return (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 16px', background: '#fff',
                        border: '1px solid #E2E8F0', borderRadius: 10,
                        boxShadow: '0 1px 3px rgba(0,0,0,.04)',
                      }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                          background: '#F1F5F9', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: 16,
                        }}>
                          {icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B', marginBottom: 2 }}>
                            {title || '—'}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {[comm.description, comm.time].filter(Boolean).join(' — ')}
                          </div>
                        </div>
                        {comm.status && (
                          <span style={{
                            fontSize: 12, fontWeight: 600, color: statusColor,
                            flexShrink: 0, paddingLeft: 8,
                          }}>
                            {comm.status.charAt(0).toUpperCase() + comm.status.slice(1)}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Follow-ups ── */}
          {activeTab === 'followups' && (
            <div style={{ padding: '20px 4px' }}>
              {followups.length === 0 ? (
                <div className="td-tab-placeholder">No follow-up calls linked to this ticket.</div>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
                    {followups.length} follow-up call{followups.length === 1 ? '' : 's'} linked to this ticket
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {followups.map((f, i) => {
                      const swaps = Array.isArray(f.swap) ? f.swap : []
                      return (
                        <div key={f.id || i} style={{
                          padding: '14px 16px', background: '#fff',
                          border: '1px solid #E2E8F0', borderRadius: 10,
                          boxShadow: '0 1px 3px rgba(0,0,0,.04)',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                            <span style={{
                              fontSize: 10, fontWeight: 600, color: '#0090D9',
                              background: '#EFF6FF', border: '1px solid #BFDBFE',
                              borderRadius: 6, padding: '2px 8px',
                            }}>
                              Follow-up #{f.follow_up_number ?? i + 1}
                            </span>
                            <span style={{ fontSize: 11, color: '#94A3B8' }}>
                              {[f.date, f.time].filter(Boolean).join(' · ')}
                            </span>
                          </div>

                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 12, fontSize: 12 }}>
                            <span style={{ fontWeight: 600, color: '#1E293B' }}>👤 {rider.name || '—'}</span>
                            {rider.number && <span style={{ color: '#6B7280' }}>📞 {rider.number}</span>}
                          </div>

                          {swaps.length === 0 ? (
                            <div className="td-tab-placeholder">No swap details for this follow-up.</div>
                          ) : (
                            /* overflowY must stay 'visible' — 'auto' would clip the reason tooltip */
                            <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5 }}>
                                <thead>
                                  <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
                                    {['Transaction ID', 'Station', 'Start', 'End', 'Duration', 'Status', 'Error Code', 'Reason'].map(h => (
                                      <th key={h} style={{ padding: '5px 8px', fontSize: 9.5, fontWeight: 500, color: '#94A3B8', borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap' }}>
                                        {h}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {swaps.map((s, si) => {
                                    const isOk = String(s.status || '').toLowerCase() === 'ok'
                                    const reason = s.status_reason || ''
                                    return (
                                      <tr key={s.id || si} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                        <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontWeight: 400, color: '#374151', whiteSpace: 'nowrap' }}>{s.transaction_id || '—'}</td>
                                        <td style={{ padding: '5px 8px', fontWeight: 400, color: '#374151', whiteSpace: 'nowrap' }}>{s.station?.name || s.station?.station_code || '—'}</td>
                                        <td style={{ padding: '5px 8px', fontWeight: 400, color: '#374151', whiteSpace: 'nowrap' }}>
                                          {s.swap_start ? new Date(s.swap_start).toLocaleString() : '—'}
                                        </td>
                                        <td style={{ padding: '5px 8px', fontWeight: 400, color: '#374151', whiteSpace: 'nowrap' }}>
                                          {s.swap_end ? new Date(s.swap_end).toLocaleString() : '—'}
                                        </td>
                                        <td style={{ padding: '5px 8px', fontWeight: 400, color: '#374151', whiteSpace: 'nowrap' }}>
                                          {s.swap_duration_seconds != null ? `${s.swap_duration_seconds}s` : '—'}
                                        </td>
                                        <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>
                                          <span style={{
                                            fontSize: 9.5, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                                            background: isOk ? '#DCFCE7' : '#FEE2E2',
                                            color: isOk ? '#166534' : '#991B1B',
                                          }}>
                                            {isOk ? 'OK' : 'Error'}
                                          </span>
                                        </td>
                                        <td style={{ padding: '5px 8px', fontWeight: 400, color: '#6B7280' }}>
                                          <span className={ s.error_code  ? 'td-reason-cell' : ''} style={{ position: 'relative' }}>
                                            <span style={{
                                              display: 'block', maxWidth: 260, overflow: 'hidden',
                                              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                              { s.error_code  || '—'}
                                            </span>
                                            { s.error_code  && <span className="td-reason-tooltip">{ s.error_code }</span>}
                                          </span>
                                        </td>
                                        <td style={{ padding: '5px 8px', fontWeight: 400, color: '#6B7280' }}>
                                          <span className={reason ? 'td-reason-cell' : ''} style={{ position: 'relative' }}>
                                            <span style={{
                                              display: 'block', maxWidth: 260, overflow: 'hidden',
                                              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                              {reason || '—'}
                                            </span>
                                            {reason && <span className="td-reason-tooltip">{reason}</span>}
                                          </span>
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Resolution ── */}
          {activeTab === 'resolution' && (
            <div style={{ padding: '20px 4px' }}>
              {!resolutionData ? (
                <div className="td-tab-placeholder">No resolution data yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                  {/* Team-specific resolution fields */}
                  {resolutionData.resolution_fields && Object.keys(resolutionData.resolution_fields).length > 0 && (
                    <SectionCard title="Resolution Fields">
                      {Object.entries(resolutionData.resolution_fields).map(([key, value]) => (
                        <InfoRow key={key} label={formatFieldLabel(key)} value={String(value || '—')} />
                      ))}
                    </SectionCard>
                  )}

                  {/* Common summary fields */}
                  <SectionCard title="Resolution Summary">
                    <InfoRow label="Ticket Status" value={displayStatus(resolutionData.ticket_status)} />
                    <InfoRow label="Issue Resolved">
                      {resolutionData.issue_resolved ? (
                        <span style={{
                          fontWeight: 600,
                          color: resolutionData.issue_resolved === 'yes' ? '#16A34A' : '#DC2626',
                        }}>
                          {resolutionData.issue_resolved === 'yes' ? 'Yes' : 'No'}
                        </span>
                      ) : '—'}
                    </InfoRow>
                    {resolutionData.remarks && (
                      <InfoRow label="Remarks" value={resolutionData.remarks} />
                    )}
                  </SectionCard>

                  {/* Uploaded attachments */}
                  {resolutionData.attachment_type && (
                    <SectionCard title={formatFieldLabel(resolutionData.attachment_type)}>
                      {resolutionAttachmentsLoading ? (
                        <div style={{ fontSize: 13, color: '#94A3B8', padding: '8px 0' }}>Loading attachments…</div>
                      ) : resolutionAttachments.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#94A3B8', padding: '8px 0' }}>No attachments uploaded.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                          {resolutionAttachments.map((att, i) => {
                            const fileName = att.file_name ? att.file_name.split('/').pop() : `attachment-${i + 1}`
                            const ext = fileName.split('.').pop()?.toLowerCase()
                            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
                            return (
                              <div key={att.id || i} style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '10px 14px', background: '#F8FAFC',
                                border: '1px solid #E2E8F0', borderRadius: 8,
                              }}>
                                {isImage && att.file ? (
                                  <img src={att.file} alt={fileName} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                                ) : (
                                  <span style={{ fontSize: 22, flexShrink: 0 }}>📄</span>
                                )}
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                  <div style={{ fontSize: 13, fontWeight: 500, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {fileName}
                                  </div>
                                  {att.attachment_type && (
                                    <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2, textTransform: 'capitalize' }}>
                                      {att.attachment_type.replace(/_/g, ' ')}
                                    </div>
                                  )}
                                </div>
                                <a href={att.file} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: 12, fontWeight: 600, color: '#0090D9', textDecoration: 'none', flexShrink: 0, padding: '4px 10px', border: '1px solid #BAE6FD', borderRadius: 6, background: '#F0F9FF' }}>
                                  {isImage ? 'View ↗' : 'Download ↗'}
                                </a>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </SectionCard>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Evidence (attachments) ── */}
          {activeTab === 'evidence' && (
            <div style={{ padding: '20px 4px' }}>
              {/* Upload section */}
              <div style={{
                marginBottom: 20, padding: '14px 16px',
                background: '#F8FAFC', border: '1.5px dashed #CBD5E1', borderRadius: 10,
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                  Upload Evidence
                </div>
                <input
                  ref={evidenceFileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => {
                    const files = Array.from(e.target.files || [])
                    if (files.length) setEvidenceFiles(prev => [...prev, ...files])
                    e.target.value = ''
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => evidenceFileInputRef.current?.click()}
                    disabled={evidenceUploading}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '7px 14px', border: '1.5px solid #CBD5E1',
                      borderRadius: 7, background: '#fff', cursor: 'pointer',
                      fontSize: 12, color: '#64748B', fontWeight: 500,
                    }}
                  >
                    📎 Choose Files
                  </button>
                  {evidenceFiles.length > 0 && (
                    <button
                      type="button"
                      disabled={evidenceUploading}
                      onClick={async () => {
                        setEvidenceUploading(true)
                        setEvidenceUploadError('')
                        try {
                          const formData = new FormData()
                          evidenceFiles.forEach(f => formData.append('files', f))
                          await fetchJson(
                            `/api/tickets/ticket/add-attachments/${ticketId}/?attachment_type=evidence`,
                            { method: 'POST', body: formData },
                          )
                          setEvidenceFiles([])
                          setEvidenceRefresh(n => n + 1)
                        } catch (err) {
                          setEvidenceUploadError(err?.message || 'Upload failed. Please try again.')
                        } finally {
                          setEvidenceUploading(false)
                        }
                      }}
                      style={{
                        padding: '7px 16px', borderRadius: 7, border: 'none',
                        background: '#0090D9', color: '#fff', fontWeight: 600,
                        fontSize: 12, cursor: 'pointer',
                      }}
                    >
                      {evidenceUploading ? 'Uploading…' : `Upload ${evidenceFiles.length} file${evidenceFiles.length > 1 ? 's' : ''}`}
                    </button>
                  )}
                </div>

                {evidenceFiles.length > 0 && (
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {evidenceFiles.map((f, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '5px 10px', background: '#fff', borderRadius: 6,
                        border: '1px solid #E2E8F0',
                      }}>
                        <span style={{ fontSize: 12, flex: 1, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          📄 {f.name}
                        </span>
                        <span style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }}>
                          {f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(0)} KB` : `${(f.size / (1024 * 1024)).toFixed(1)} MB`}
                        </span>
                        <button
                          type="button"
                          onClick={() => setEvidenceFiles(prev => prev.filter((_, j) => j !== i))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, padding: 0, lineHeight: 1 }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {evidenceUploadError && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#DC2626' }}>{evidenceUploadError}</div>
                )}
              </div>

              {/* ── Ticket Evidence (from ticket detail API) ── */}
              {ticketEvidence.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>
                    Ticket Evidence
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {ticketEvidence.map((item, i) => {
                      const rawName = item.file_name || item.file || ''
                      const fileName = rawName.split('/').pop() || `file-${i + 1}`
                      const ext = fileName.split('.').pop().toLowerCase().split('?')[0]
                      const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)
                        || (!fileName.includes('.') && fileName.toLowerCase().includes('image'))
                      const typeLabel = item.attachment_type
                        ? item.attachment_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                        : null
                      return (
                        <div key={item.id || i} style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '10px 14px', background: '#fff',
                          border: '1px solid #E2E8F0', borderRadius: 8,
                        }}>
                          {isImg ? (
                            <img
                              src={item.file} alt={fileName}
                              style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, flexShrink: 0, cursor: 'pointer' }}
                              onClick={() => setViewerSrc(item.file)}
                            />
                          ) : (
                            <span style={{ fontSize: 22, flexShrink: 0 }}>📄</span>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fileName}
                            </div>
                            {typeLabel && (
                              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{typeLabel}</div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => isImg ? setViewerSrc(item.file) : window.open(item.file, '_blank', 'noopener,noreferrer')}
                            style={{
                              fontSize: 12, fontWeight: 600, color: '#0090D9', flexShrink: 0,
                              padding: '4px 10px', border: '1px solid #BAE6FD', borderRadius: 6,
                              background: '#F0F9FF', cursor: 'pointer',
                            }}
                          >
                            {isImg ? 'View ↗' : 'Download ↗'}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Attachments list */}
              {attachmentsLoading ? (
                <div className="td-tab-placeholder">Loading attachments…</div>
              ) : attachments.length === 0 ? (
                <div className="td-tab-placeholder">No attachments for this ticket.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {attachments.map((att, i) => {
                    const fileName = att.file_name
                      ? att.file_name.split('/').pop()
                      : `attachment-${i + 1}`
                    const ext = fileName.split('.').pop()?.toLowerCase()
                    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
                    return (
                      <div key={att.id || i} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 14px', background: '#fff',
                        border: '1px solid #E2E8F0', borderRadius: 8,
                      }}>
                        <span style={{ fontSize: 20, flexShrink: 0 }}>
                          {isImage ? '🖼️' : '📄'}
                        </span>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {fileName}
                          </div>
                        </div>
                        <a
                          href={att.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 12, fontWeight: 600, color: '#0090D9',
                            textDecoration: 'none', flexShrink: 0,
                            padding: '4px 10px', border: '1px solid #BAE6FD',
                            borderRadius: 6, background: '#F0F9FF',
                          }}
                        >
                          Download ↗
                        </a>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Swap Details ── */}
          {activeTab === 'swaps' && (
            <div className="td-main">
              <SectionCard title={(() => {
                // created_at = when these swap records were fetched; same for every row, so show it once (latest wins if they differ)
                const fetchedAt = swapDetails
                  .map(s => s?.created_at ? new Date(s.created_at) : null)
                  .filter(d => d && !isNaN(d))
                  .sort((x, y) => y - x)[0]
                return (
                  <span style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 }}>
                    <span>Swap Details{swapDetails.length ? ` (${swapDetails.length})` : ''}</span>
                    {fetchedAt && (
                      <span style={{ fontSize: 12, fontWeight: 500, color: '#64748B', textTransform: 'none', letterSpacing: 0 }}>
                        Fetched at: {fetchedAt.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </span>
                )
              })()}>
                {swapDetails.length === 0 ? (
                  <div className="td-tab-placeholder">No swap details for this ticket.</div>
                ) : (
                  /* overflowY must stay 'visible' — 'auto' would clip the reason tooltip */
                  <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
                          {['#', 'Transaction ID', 'Station', 'Swap Type', 'Start', 'End', 'Duration', 'Status', 'Error Code', 'Reason'].map(h => (
                            <th key={h} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.3px', borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap' }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {swapDetails.map((s, si) => {
                          const isOk = String(s.status || '').toLowerCase() === 'ok'
                          const reason = s.status_reason || ''
                          // Highlight the swap this ticket was raised against
                          const isTicketSwap = !!info.cnc_transaction_id && s.transaction_id === info.cnc_transaction_id
                          const dur = s.swap_duration_seconds
                          const durLabel = dur == null ? '—' : (dur >= 60 ? `${Math.floor(dur / 60)}m ${dur % 60}s` : `${dur}s`)
                          const station = s.station && typeof s.station === 'object'
                            ? (s.station.name || s.station.station_code || s.station.slug)
                            : s.station
                          const cell = { padding: '8px 10px', color: '#334155', whiteSpace: 'nowrap' }
                          return (
                            <tr key={s.id || si} style={{ borderBottom: '1px solid #F1F5F9', background: isTicketSwap ? '#EFF6FF' : undefined }}>
                              <td style={{ ...cell, color: '#94A3B8' }}>{si + 1}</td>
                              <td style={{ ...cell, fontFamily: 'monospace', fontWeight: isTicketSwap ? 700 : 400 }}>
                                {s.transaction_id || '—'}
                                {isTicketSwap && (
                                  <span style={{ marginLeft: 6, fontFamily: 'inherit', fontSize: 10, fontWeight: 600, color: '#0090D9', background: '#DBEAFE', borderRadius: 10, padding: '1px 6px' }}>
                                    This ticket
                                  </span>
                                )}
                              </td>
                              <td style={cell}>{station || '—'}</td>
                              <td style={cell}>{s.swap_type || '—'}</td>
                              <td style={cell}>{s.swap_start ? new Date(s.swap_start).toLocaleString('en-IN') : '—'}</td>
                              <td style={cell}>{s.swap_end ? new Date(s.swap_end).toLocaleString('en-IN') : '—'}</td>
                              <td style={cell}>{durLabel}</td>
                              <td style={{ ...cell }}>
                                <span style={{
                                  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                                  background: isOk ? '#DCFCE7' : '#FEE2E2',
                                  color: isOk ? '#166534' : '#991B1B',
                                }}>
                                  {isOk ? 'OK' : (s.status ? String(s.status).toUpperCase() : 'Error')}
                                </span>
                              </td>
                              <td style={{ padding: '8px 10px', color: '#6B7280' }}>
                                <span className={s.error_code ? 'td-reason-cell' : ''} style={{ position: 'relative' }}>
                                  <span style={{ display: 'block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {s.error_code || '—'}
                                  </span>
                                  {s.error_code && <span className="td-reason-tooltip">{s.error_code}</span>}
                                </span>
                              </td>
                              <td style={{ padding: '8px 10px', color: '#6B7280' }}>
                                <span className={reason ? 'td-reason-cell' : ''} style={{ position: 'relative' }}>
                                  <span style={{ display: 'block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {reason || '—'}
                                  </span>
                                  {reason && <span className="td-reason-tooltip">{reason}</span>}
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>
            </div>
          )}

          {/* ── Location ── */}
          {activeTab === 'location' && (
            <div className="td-main">
              <SectionCard title="Location">
                {locationLoading ? (
                  <div className="td-tab-placeholder">Loading location…</div>
                ) : locationError ? (
                  <div className="td-tab-placeholder">{locationError}</div>
                ) : (() => {
                  const entries = (Array.isArray(locationData) ? locationData : [])
                    .filter(entry => entry?.latitude && entry?.longitude)
                  if (entries.length === 0) {
                    return <div className="td-tab-placeholder">No location data for this ticket.</div>
                  }
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {entries.map((entry, idx) => (
                        <div key={idx} style={{
                          display: 'flex', flexDirection: 'column', gap: 14,
                          padding: '16px 18px', background: '#F8FAFC',
                          border: '1px solid #E2E8F0', borderRadius: 10,
                        }}>
                          <span style={{
                            alignSelf: 'flex-start', fontSize: 11, fontWeight: 700, color: '#0090D9',
                            background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 20,
                            padding: '3px 10px', textTransform: 'uppercase', letterSpacing: '.4px',
                          }}>
                            {locationTypeLabel(entry.type)}
                          </span>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1 }}>📍</span>
                            <div style={{ fontSize: 14, color: '#1E293B', fontWeight: 600, lineHeight: 1.5 }}>
                              {entry.address || entry.metadata?.address || '—'}
                            </div>
                          </div>
                          {entry.metadata && (
                            <div style={{
                              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10,
                              paddingTop: 12, borderTop: '1px solid #E2E8F0',
                            }}>
                              {entry.metadata.city && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.4px' }}>City</span>
                                  <span style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>{entry.metadata.city}</span>
                                </div>
                              )}
                              {entry.metadata.state && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.4px' }}>State</span>
                                  <span style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>{entry.metadata.state}</span>
                                </div>
                              )}
                              {entry.metadata.postal_code && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.4px' }}>Postal Code</span>
                                  <span style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>{entry.metadata.postal_code}</span>
                                </div>
                              )}
                              {entry.metadata.country && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.4px' }}>Country</span>
                                  <span style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>{entry.metadata.country}</span>
                                </div>
                              )}
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingTop: 12, borderTop: '1px solid #E2E8F0' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.4px' }}>Coordinates</span>
                              <span style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>
                                {entry.latitude}, {entry.longitude}
                              </span>
                            </div>
                            <a
                              href={`https://www.google.com/maps?q=${entry.latitude},${entry.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: 12, fontWeight: 600, color: '#0090D9', textDecoration: 'none',
                                padding: '6px 12px', border: '1px solid #BAE6FD', borderRadius: 6,
                                background: '#F0F9FF', flexShrink: 0, whiteSpace: 'nowrap',
                              }}
                            >
                              View on Map ↗
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })()}
              </SectionCard>
            </div>
          )}

          {/* ── Remote Session (Remote Battery Unlock · Stage 2) ── */}
          {activeTab === REMOTE_SESSION_TAB && (
            <RemoteSessionTab
              fetchJson={fetchJson}
              ticketId={ticketId}
              info={info}
              stationCode={info.station_code || info.station_id}
              checklist={checklist}
              loading={checklistLoading}
              error={checklistError}
              levels={remoteSessionLevels}
              ticketClosed={ticketClosed}
              onSaved={() => { setChecklistRefresh(n => n + 1); setDetailRefresh(n => n + 1) }}
            />
          )}
        </div>

        <aside className="td-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <TatDetails tat={tat} stage={info.ticket_stage} />

          {/* Re-assign Ticket — only for ERT users */}
          {isErtUser && (
            <button
              type="button"
              onClick={() => setReassignOpen(true)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                padding: '10px 14px', border: '1.5px solid #0090D9', borderRadius: 9,
                background: '#F0F9FF', color: '#0090D9', fontWeight: 700, fontSize: 13,
                cursor: 'pointer', transition: 'background .15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#E0F2FE'}
              onMouseLeave={e => e.currentTarget.style.background = '#F0F9FF'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <polyline points="16 11 18 13 22 9"/>
              </svg>
              Re-assign Ticket
            </button>
          )}

          {/* Resolution collapsible */}
          {resolutionData && (
            <div style={{ border: '1px solid #E0E7EF', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
              <button
                onClick={() => setResolutionOpen(o => !o)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px', background: resolutionOpen ? '#F0F9FF' : '#fff',
                  border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1E293B',
                  borderBottom: resolutionOpen ? '1px solid #E0E7EF' : 'none',
                }}
              >
                <span>Resolution Details</span>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="#64748B" strokeWidth="2"
                  style={{ transform: resolutionOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>
                  <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {resolutionOpen && (
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {resolutionData && resolutionData.resolution_fields && Object.keys(resolutionData.resolution_fields).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {Object.entries(resolutionData.resolution_fields).map(([key, value]) => (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500, flexShrink: 0 }}>{formatFieldLabel(key)}</span>
                          <span style={{ fontSize: 13, color: '#111827', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{String(value || '—')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {resolutionData && (
                    <>
                      <div style={{ borderTop: '1px solid #E2E8F0' }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>Ticket Status</span>
                          <span style={{ fontSize: 13, color: '#111827', fontWeight: 600 }}>{displayStatus(resolutionData.ticket_status ||resolutionData.status )}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>Issue Resolved</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: resolutionData.issue_resolved === 'yes' ? '#16A34A' : resolutionData.issue_resolved === 'no' ? '#DC2626' : '#6B7280' }}>
                            {resolutionData.issue_resolved === 'yes' ? 'Yes' : resolutionData.issue_resolved === 'no' ? 'No' : '—'}
                          </span>
                        </div>
                        {resolutionData.remarks && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>Remarks</span>
                            <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.5 }}>{resolutionData.remarks}</div>
                          </div>
                        )}
                      </div>
                      {resolutionData.attachment_type && (
                        <>
                          <div style={{ borderTop: '1px solid #E2E8F0' }} />
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                            {formatFieldLabel(resolutionData.attachment_type)}
                          </div>
                          {resolutionAttachmentsLoading ? (
                            <div style={{ fontSize: 12, color: '#94A3B8' }}>Loading…</div>
                          ) : resolutionAttachments.length === 0 ? (
                            <div style={{ fontSize: 12, color: '#94A3B8' }}>No attachments uploaded.</div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {resolutionAttachments.map((att, i) => {
                                const fileName = att.file_name ? att.file_name.split('/').pop() : `attachment-${i + 1}`
                                const ext = fileName.split('.').pop()?.toLowerCase()
                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
                                return (
                                  <div key={att.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#F8FAFC', borderRadius: 8, border: '1px solid #E2E8F0' }}>
                                    <span style={{ fontSize: 18, flexShrink: 0 }}>{isImage ? '🖼️' : '📄'}</span>
                                    <span style={{ fontSize: 12, flex: 1, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</span>
                                    <a href={att.file} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: '#0090D9', textDecoration: 'none', flexShrink: 0 }}>
                                      {isImage ? 'View' : 'Download'} ↗
                                    </a>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Closure Data collapsible — shown for all users */}
          {closureData && (
            <div style={{ border: '1px solid #E0E7EF', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
              <button
                onClick={() => setClosureOpen(o => !o)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px', background: closureOpen ? '#F0F9FF' : '#fff',
                  border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1E293B',
                  borderBottom: closureOpen ? '1px solid #E0E7EF' : 'none',
                }}
              >
                <span>Closure Data</span>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="#64748B" strokeWidth="2"
                  style={{ transform: closureOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>
                  <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {closureOpen && (() => {
                const entries = Object.entries(closureData || {}).filter(
                  ([, val]) => val !== null && val !== undefined && val !== ''
                )

                if (entries.length === 0) {
                  return (
                    <div style={{ padding: '16px', fontSize: 13, color: '#94A3B8', textAlign: 'center' }}>
                      No closure data
                    </div>
                  )
                }

                return (
                  <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {entries.map(([key, val]) => {
                      let valueNode
                      if (typeof val === 'boolean') {
                        valueNode = (
                          <span style={{ fontSize: 13, fontWeight: 600, color: val ? '#16A34A' : '#DC2626' }}>
                            {val ? 'Yes' : 'No'}
                          </span>
                        )
                      } else if (key === 'bp_soc') {
                        valueNode = (
                          <span style={{ fontSize: 13, color: '#111827', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{`${val}%`}</span>
                        )
                      } else {
                        const text = typeof val === 'object' ? JSON.stringify(val) : String(val)
                        valueNode = (
                          <span style={{ fontSize: 13, color: '#111827', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{text}</span>
                        )
                      }
                      return (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500, flexShrink: 0 }}>{formatFieldLabel(key)}</span>
                          {valueNode}
                        </div>
                      )
                    })}
                  </div>
                )
              })()}
            </div>
          )}

          {!isErtUser && (
            <QuickActions
              status={statusValue}
              ticketId={ticketId}
              fetchJson={fetchJson}
              onStatusUpdated={(s) => { setStatusValue(s); setDetailRefresh(n => n + 1) }}
            />
          )}
        </aside>
      </div>

      {/* ── Re-assign Ticket Modal ── */}
      {reassignOpen && (
        <ReassignModal
          ticketId={ticketId}
          fetchJson={fetchJson}
          onClose={() => setReassignOpen(false)}
          onSuccess={() => setDetailRefresh(n => n + 1)}
        />
      )}

      {/* ── ERT Ticket Closure Modal ── */}
      {ertClosureOpen && (
        <ErtClosureModal
          onClose={() => setErtClosureOpen(false)}
          onSubmit={handleErtClosureSubmit}
        />
      )}

      {/* ── Image Viewer ── */}
      {viewerSrc && (
        <div
          onClick={() => setViewerSrc(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 20000,
            background: 'rgba(0,0,0,.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, cursor: 'zoom-out',
          }}
        >
          <button
            type="button"
            onClick={() => setViewerSrc(null)}
            style={{
              position: 'absolute', top: 16, right: 20,
              background: 'rgba(255,255,255,.15)', border: 'none',
              color: '#fff', fontSize: 28, lineHeight: 1,
              cursor: 'pointer', borderRadius: 6, padding: '2px 10px',
            }}
          >
            ×
          </button>
          <a
            href={viewerSrc} target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', top: 16, right: 64,
              background: 'rgba(255,255,255,.15)', border: 'none',
              color: '#fff', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', borderRadius: 6, padding: '4px 12px',
              textDecoration: 'none',
            }}
          >
            Open ↗
          </a>
          <img
            src={viewerSrc}
            alt="Evidence"
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '90vw', maxHeight: '90vh',
              borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,.6)',
              cursor: 'default',
            }}
          />
        </div>
      )}

      {/* ── Edit Ticket Modal ── */}
      {editOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          overflowY: 'auto', padding: '24px 16px',
        }}
          onClick={e => { if (e.target === e.currentTarget) setEditOpen(false) }}
        >
          <div style={{
            background: '#F3F4F6', borderRadius: 12, width: '100%', maxWidth: 820,
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)', position: 'relative',
            minHeight: 'min-content',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: '1px solid #E5E7EB',
              background: '#fff', borderRadius: '12px 12px 0 0',
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Edit Ticket — {ticketNumber}</div>
              <button onClick={() => setEditOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#6B7280', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '0 4px 4px' }}>
              <CreateTicketPage
                fetchJson={fetchJson}
                editMode={true}
                editTicketId={ticketId}
                onEditSuccess={() => { setEditOpen(false); setDetailRefresh(n => n + 1) }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
